/**
 * ConditionalElsePatternStep Component - For CONDITIONAL_ELSE pattern configuration
 * Used when user selected "Sometimes data is missing, hide or show different text" → "Show different text instead"
 */

import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Shuffle, Users, CheckCircle2, ArrowRight, Lightbulb, AlertTriangle, Code, Eye, Copy, Zap, TestTube, Split } from 'lucide-react'
import { WizardState } from '../../../../types/macros'
import DataKeyInput from './shared/DataKeyInput'

export interface ConditionalElsePatternStepProps {
  wizardState: WizardState
  onUpdateState: (updates: Partial<WizardState>) => void
  onBack: () => void
}

const ConditionalElsePatternStep: React.FC<ConditionalElsePatternStepProps> = ({
  wizardState,
  onUpdateState,
  onBack
}) => {
  const [localState, setLocalState] = useState({
    dataKey: wizardState.patternData.conditionalElse?.dataKey || '',
    conditionalContent: wizardState.patternData.conditionalElse?.conditionalContent || '',
    elseContent: wizardState.patternData.conditionalElse?.elseContent || '',
    description: wizardState.patternData.conditionalElse?.description || ''
  })
  const [testValue, setTestValue] = useState('')
  const [testMode, setTestMode] = useState<'with-data' | 'without-data'>('with-data')

  // Update parent state when local state changes
  React.useEffect(() => {
    onUpdateState({
      patternData: {
        ...wizardState.patternData,
        conditionalElse: {
          dataKey: localState.dataKey,
          conditionalContent: localState.conditionalContent,
          elseContent: localState.elseContent,
          description: localState.description
        }
      }
    })
  }, [localState, onUpdateState, wizardState.patternData])

  // Validation
  const isValid = React.useMemo(() => {
    return localState.dataKey.trim() !== '' && 
           /^[a-zA-Z][a-zA-Z0-9_]*$/.test(localState.dataKey) &&
           localState.conditionalContent.trim() !== '' &&
           localState.elseContent.trim() !== ''
  }, [localState.dataKey, localState.conditionalContent, localState.elseContent])

  // Generate syntax
  const generatedSyntax = React.useMemo(() => {
    if (!localState.dataKey || !localState.conditionalContent || !localState.elseContent) return ''
    return `{{#if ${localState.dataKey}}}${localState.conditionalContent}{{else}}${localState.elseContent}{{/if}}`
  }, [localState.dataKey, localState.conditionalContent, localState.elseContent])

  // Interactive templates
  const templates = [
    {
      id: 'personalized_greeting',
      title: 'Personalized vs Generic Greeting',
      description: 'Show personalized greeting when name is available, generic otherwise',
      dataKey: 'first_name',
      conditionalContent: 'Welcome back, {{first_name}}!',
      elseContent: 'Welcome to our store!',
      withData: { value: 'Sarah', result: 'Welcome back, Sarah!' },
      withoutData: { result: 'Welcome to our store!' }
    },
    {
      id: 'loyalty_program',
      title: 'VIP vs Standard Treatment',
      description: 'Different messaging for VIP and regular customers',
      dataKey: 'vip_status',
      conditionalContent: '⭐ VIP Exclusive: Free shipping on all orders!',
      elseContent: 'Free shipping on orders over $50',
      withData: { value: 'Premium', result: '⭐ VIP Exclusive: Free shipping on all orders!' },
      withoutData: { result: 'Free shipping on orders over $50' }
    },
    {
      id: 'location_shipping',
      title: 'Location-Based Shipping',
      description: 'Specific shipping for known locations, generic for unknown',
      dataKey: 'city',
      conditionalContent: 'Same-day delivery available in {{city}}!',
      elseContent: 'Fast nationwide shipping available',
      withData: { value: 'New York', result: 'Same-day delivery available in New York!' },
      withoutData: { result: 'Fast nationwide shipping available' }
    },
    {
      id: 'points_balance',
      title: 'Points vs Cash Discount',
      description: 'Show points discount for members, cash discount for others',
      dataKey: 'points_balance',
      conditionalContent: 'Use your {{points_balance}} points for instant savings!',
      elseContent: 'Join our rewards program and save 10%',
      withData: { value: '500', result: 'Use your 500 points for instant savings!' },
      withoutData: { result: 'Join our rewards program and save 10%' }
    }
  ]

  const handleTemplateSelect = (template: typeof templates[0]) => {
    setLocalState({
      dataKey: template.dataKey,
      conditionalContent: template.conditionalContent,
      elseContent: template.elseContent,
      description: template.description
    })
    setTestValue(template.withData.value)
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const getTestResult = () => {
    if (testMode === 'with-data' && testValue) {
      return localState.conditionalContent.replace(`{{${localState.dataKey}}}`, testValue)
    } else {
      return localState.elseContent
    }
  }

  // Content suggestions based on data key
  const contentSuggestions = React.useMemo(() => {
    const key = localState.dataKey.toLowerCase()
    
    if (key.includes('name') || key.includes('first')) {
      return {
        personalized: [`Welcome back, {{${localState.dataKey}}}!`, `Hi {{${localState.dataKey}}}, thanks for returning!`],
        generic: ['Welcome back!', 'Thanks for visiting!', 'Hello there!']
      }
    }
    if (key.includes('tier') || key.includes('level')) {
      return {
        personalized: [`{{${localState.dataKey}}} Member Benefits:`, `Exclusive {{${localState.dataKey}}} Offers:`],
        generic: ['Member Benefits:', 'Exclusive Member Offers:', 'Special Offers:']
      }
    }
    if (key.includes('points') || key.includes('balance')) {
      return {
        personalized: [`You have {{${localState.dataKey}}} points!`, `Your balance: {{${localState.dataKey}}}`],
        generic: ['Check your points balance!', 'Rewards available!', 'Points ready to use!']
      }
    }
    
    return {
      personalized: [`Welcome, {{${localState.dataKey}}}!`, `Hi {{${localState.dataKey}}},`],
      generic: ['Welcome!', 'Hello!', 'Thanks for visiting!']
    }
  }, [localState.dataKey])

  // Content consistency warnings
  const consistencyWarnings = React.useMemo(() => {
    const warnings = []
    
    if (localState.conditionalContent && localState.elseContent) {
      // Check for tone consistency
      const personalizedTone = /(!|welcome|hi|hello|thanks)/i.test(localState.conditionalContent)
      const genericTone = /(!|welcome|hi|hello|thanks)/i.test(localState.elseContent)
      
      if (personalizedTone && !genericTone) {
        warnings.push('Consider matching the friendly tone in both messages')
      }
      
      // Check for length consistency
      const lengthDiff = Math.abs(localState.conditionalContent.length - localState.elseContent.length)
      if (lengthDiff > 50) {
        warnings.push('Consider similar message lengths for consistent visual appearance')
      }
      
      // Check for action consistency
      const personalizedHasAction = /click|tap|shop|browse|get|claim/i.test(localState.conditionalContent)
      const genericHasAction = /click|tap|shop|browse|get|claim/i.test(localState.elseContent)
      
      if (personalizedHasAction !== genericHasAction) {
        warnings.push('Include similar calls-to-action in both messages for consistency')
      }
    }
    
    return warnings
  }, [localState.conditionalContent, localState.elseContent])

  const handlePersonalizedSuggestion = (suggestion: string) => {
    setLocalState(prev => ({ ...prev, conditionalContent: suggestion }))
  }

  const handleGenericSuggestion = (suggestion: string) => {
    setLocalState(prev => ({ ...prev, elseContent: suggestion }))
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-full mb-4">
          <Shuffle className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-[#1A2B49] dark:text-white mb-2">
          Conditional-Else Pattern Setup
        </h2>
        <p className="text-[#6E6D7A] dark:text-gray-400 max-w-md mx-auto">
          Perfect for inclusive messaging! Show personalized content when available, 
          with a welcoming fallback for everyone else.
        </p>
      </div>

      {/* How It Works - Educational Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-6"
      >
        <div className="flex items-start gap-4">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
            <Lightbulb className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-indigo-800 dark:text-indigo-300 mb-3">
              How Conditional-Else Patterns Work
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3 text-sm text-indigo-700 dark:text-indigo-400">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <p><strong>When data exists:</strong> Shows personalized content with data inserted</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p><strong>When data is missing:</strong> Shows the alternative generic content instead</p>
                </div>
              </div>
              <div className="space-y-3 text-sm text-indigo-700 dark:text-indigo-400">
                <div className="flex items-start gap-2">
                  <Users className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <p><strong>Everyone gets a message</strong> - no one sees empty spaces</p>
                </div>
                <div className="flex items-start gap-2">
                  <Split className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                  <p><strong>Perfect for inclusive,</strong> welcoming experiences</p>
                </div>
              </div>
            </div>
            <div className="mt-4 p-3 bg-white dark:bg-indigo-950/50 rounded-lg">
              <p className="text-xs font-mono text-indigo-600 dark:text-indigo-400">
                {'{{#if data_key}}'}Personalized content here{'{{else}}'}Generic content here{'{{/if}}'}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Configuration Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border-2 border-indigo-200 dark:border-indigo-800"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
            <Code className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <h3 className="text-xl font-semibold text-[#1A2B49] dark:text-white">
            Configure Your Pattern
          </h3>
        </div>

        <div className="space-y-6">
          {/* Data Key Input */}
          <DataKeyInput
            value={localState.dataKey}
            onChange={(value) => setLocalState(prev => ({ ...prev, dataKey: value }))}
            platform={wizardState.platform}
            label="Data Key"
            placeholder="e.g., first_name, loyalty_tier"
            helpText="The data field that determines which content to show"
            required={true}
          />

          {/* Personalized Content Section */}
          <div className="border border-green-200 dark:border-green-800 rounded-lg p-4 bg-green-50/50 dark:bg-green-900/20">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-5 h-5 text-green-600" />
              <h4 className="font-semibold text-green-800 dark:text-green-300">
                Personalized Content (When Data Exists)
              </h4>
            </div>
            <textarea
              value={localState.conditionalContent}
              onChange={(e) => setLocalState(prev => ({ ...prev, conditionalContent: e.target.value }))}
              placeholder="e.g., Welcome back, {{first_name}}! Your exclusive offers await."
              rows={3}
              className="w-full p-3 border-2 border-green-200 focus:border-green-500 dark:border-green-700 dark:focus:border-green-500 rounded-lg focus:outline-none bg-white dark:bg-gray-800 text-[#1A2B49] dark:text-white resize-none"
            />
            <p className="text-xs text-green-700 dark:text-green-400 mt-1">
              This shows when the data key has a value. Use {'{dataKey}'} to insert the data.
            </p>

            {/* Personalized Content Suggestions */}
            {localState.dataKey && contentSuggestions.personalized.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3"
              >
                <div className="text-xs font-medium text-green-700 dark:text-green-400 mb-2">
                  💡 Personalized suggestions:
                </div>
                <div className="space-y-2">
                  {contentSuggestions.personalized.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handlePersonalizedSuggestion(suggestion)}
                      className="block w-full text-left px-3 py-2 text-xs bg-white hover:bg-green-100 text-gray-700 hover:text-green-800 rounded border border-green-200 hover:border-green-400 transition-all"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          {/* Generic Content Section */}
          <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-4 bg-blue-50/50 dark:bg-blue-900/20">
            <div className="flex items-center gap-2 mb-3">
              <Shuffle className="w-5 h-5 text-blue-600" />
              <h4 className="font-semibold text-blue-800 dark:text-blue-300">
                Generic Content (When Data is Missing)
              </h4>
            </div>
            <textarea
              value={localState.elseContent}
              onChange={(e) => setLocalState(prev => ({ ...prev, elseContent: e.target.value }))}
              placeholder="e.g., Welcome! Discover our latest exclusive offers."
              rows={3}
              className="w-full p-3 border-2 border-blue-200 focus:border-blue-500 dark:border-blue-700 dark:focus:border-blue-500 rounded-lg focus:outline-none bg-white dark:bg-gray-800 text-[#1A2B49] dark:text-white resize-none"
            />
            <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
              This shows when the data key is empty or missing. Keep it friendly and inclusive.
            </p>

            {/* Generic Content Suggestions */}
            {localState.dataKey && contentSuggestions.generic.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3"
              >
                <div className="text-xs font-medium text-blue-700 dark:text-blue-400 mb-2">
                  💡 Generic suggestions:
                </div>
                <div className="space-y-2">
                  {contentSuggestions.generic.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleGenericSuggestion(suggestion)}
                      className="block w-full text-left px-3 py-2 text-xs bg-white hover:bg-blue-100 text-gray-700 hover:text-blue-800 rounded border border-blue-200 hover:border-blue-400 transition-all"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          {/* Consistency Warnings */}
          {consistencyWarnings.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">
                    Consistency Tips:
                  </div>
                  <ul className="space-y-1">
                    {consistencyWarnings.map((warning, index) => (
                      <li key={index} className="text-xs text-amber-700 dark:text-amber-400">
                        • {warning}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          )}

          {/* Description Input */}
          <div>
            <label className="block text-sm font-semibold text-[#1A2B49] dark:text-white mb-2">
              Description (Optional)
            </label>
            <textarea
              value={localState.description}
              onChange={(e) => setLocalState(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of what this macro does..."
              rows={3}
              className="w-full p-3 border-2 border-gray-200 focus:border-[#FE4215] dark:border-gray-700 dark:focus:border-[#FE4215] rounded-lg focus:outline-none bg-white dark:bg-gray-800 text-[#1A2B49] dark:text-white resize-none"
            />
          </div>
        </div>
      </motion.div>

      {/* Interactive Templates */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700"
      >
        <div className="flex items-center gap-3 mb-4">
          <Zap className="w-5 h-5 text-indigo-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Quick Start Templates
          </h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Click any template to instantly configure your pattern with proven examples
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {templates.map((template) => (
            <motion.div
              key={template.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4 cursor-pointer hover:shadow-md transition-all"
              onClick={() => handleTemplateSelect(template)}
            >
              <h4 className="font-medium text-indigo-900 dark:text-indigo-100 mb-2">{template.title}</h4>
              <p className="text-sm text-indigo-700 dark:text-indigo-300 mb-3">{template.description}</p>
              <div className="space-y-2">
                <div className="text-xs text-indigo-600 dark:text-indigo-400">
                  <strong>With data:</strong> {template.withData.result}
                </div>
                <div className="text-xs text-indigo-600 dark:text-indigo-400">
                  <strong>Without data:</strong> {template.withoutData.result}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Live Preview & Testing */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700"
      >
        <div className="flex items-center gap-3 mb-4">
          <Eye className="w-5 h-5 text-purple-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            See How It Looks
          </h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Test your pattern with real data to see both personalized and generic versions
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Live Syntax */}
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  Generated Syntax
                </h4>
                {generatedSyntax && (
                  <button
                    onClick={() => copyToClipboard(generatedSyntax)}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors"
                  >
                    <Copy className="w-3 h-3" />
                    Copy
                  </button>
                )}
              </div>
              <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-sm break-all">
                {generatedSyntax || 'Configure the fields above to generate syntax...'}</div>
            </div>
          </div>

          {/* Interactive Testing */}
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <TestTube className="w-4 h-4" />
                Test Both Scenarios
              </h4>
              
              <div className="space-y-4">
                {/* Test Mode Toggle */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setTestMode('with-data')}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      testMode === 'with-data'
                        ? 'bg-indigo-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    ✓ With Data
                  </button>
                  <button
                    onClick={() => setTestMode('without-data')}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      testMode === 'without-data'
                        ? 'bg-indigo-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    ✗ Without Data
                  </button>
                </div>

                {/* Test Input */}
                {testMode === 'with-data' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Test value for "{localState.dataKey || 'dataKey'}"
                    </label>
                    <input
                      type="text"
                      value={testValue}
                      onChange={(e) => setTestValue(e.target.value)}
                      placeholder="Enter test value..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                )}

                {/* Test Result */}
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Result ({testMode === 'with-data' ? 'With Data' : 'Without Data'}):
                  </p>
                  <div className={`border rounded-lg p-4 min-h-[60px] ${
                    testMode === 'with-data' 
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                      : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                  }`}>
                    <p className="text-gray-900 dark:text-white">{getTestResult() || 'Configure the fields to see results...'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Smart Tips */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6"
      >
        <div className="flex items-start gap-4">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
            <Split className="w-6 h-6 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-3">Smart Tips for Conditional-Else Patterns</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-amber-700 dark:text-amber-300">
                <div>
                  <strong>When to Use:</strong>
                  <ul className="mt-1 space-y-1">
                    <li>• Personalized vs generic greetings</li>
                    <li>• VIP vs standard messaging</li>
                    <li>• Location-specific content</li>
                  </ul>
                </div>
                <div>
                  <strong>Best Practices:</strong>
                  <ul className="mt-1 space-y-1">
                    <li>• Keep similar tone and length</li>
                    <li>• Test both scenarios</li>
                    <li>• Make generic content welcoming</li>
                  </ul>
                </div>
                <div>
                  <strong>Common Patterns:</strong>
                  <ul className="mt-1 space-y-1">
                    <li>• Name-based greetings</li>
                    <li>• Loyalty tier benefits</li>
                    <li>• Regional offers</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
      </motion.div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#6E6D7A] hover:text-[#1A2B49] dark:text-gray-400 dark:hover:text-white transition-colors"
        >
          ← Back to pattern selection
        </button>

        {isValid && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2 text-sm text-green-600"
          >
            <CheckCircle2 className="w-4 h-4" />
            Ready to preview
            <ArrowRight className="w-4 h-4" />
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default ConditionalElsePatternStep